import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { LuX } from 'react-icons/lu';
import {
    ABRIR_NOVIDADE_EVENT,
    HOME_LIVRE_EVENT,
    homeEstaLivre,
    midiaNoAr,
    novidadesDaRota,
    novidadesVigentes,
    type Novidade,
} from '../config/novidades';
import { NotificacaoService } from '../services/NotificacaoService';
import './NovidadeModal.css';

/**
 * O modal das novidades do produto. Mora no MainLayout, e não na Home, para valer em
 * qualquer tela do app — inclusive para quem entrou por um link direto. (Até 29/10/2026
 * havia um segundo motivo: no celular o login caía na Pipeline; hoje cai na Home.)
 *
 * Qual novidade: a que pede a tela em que a pessoa está (`rotas` em config/novidades.ts),
 * ou a que vale em qualquer tela. **No máximo uma por carregamento da página** — com
 * duas em cartaz, a segunda espera a próxima visita em vez de empilhar aviso sobre aviso.
 *
 * Quando ele abre sozinho:
 * - "Entendi" (ou assistir o vídeo até o fim) não deixa voltar: a marca vai para o servidor
 *   (`notification_reads`, tipo "novidade") e para o navegador;
 * - "Ver depois", o X e o Esc adiam até o dia seguinte. Na 3ª vez que a pessoa adia, ele
 *   para de abrir sozinho — a novidade continua no bloco "Novidades" da Home.
 * Na Home, ele espera a apresentação do Brik sair da frente.
 *
 * O bloco "Novidades" da Home abre o mesmo modal para rever, sem mexer em marca nenhuma.
 */

const MAX_ADIAMENTOS = 3;

interface Registro {
    vista?: boolean;
    /** Até quando o "Ver depois" segura o modal (ms). */
    adiadaAte?: number;
    adiamentos?: number;
}

const lerUsuario = () => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
};

const chaveLocal = (usuarioId: string, chave: string) => `lemeia:novidade:${usuarioId}:${chave}`;

const lerRegistro = (k: string): Registro => {
    try { return JSON.parse(localStorage.getItem(k) || '{}') ?? {}; }
    catch { return {}; }
};

const gravarRegistro = (k: string, r: Registro) => {
    try { localStorage.setItem(k, JSON.stringify(r)); } catch { /* sem armazenamento */ }
};

const amanha = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const NovidadeModal: FC = () => {
    const location = useLocation();
    const usuario = lerUsuario();
    const usuarioId = String(usuario?.id ?? usuario?.userId ?? 'anon');

    /** As candidatas desta tela, na ordem do cartaz. Mudam quando a pessoa troca de tela. */
    const candidatas = useMemo(() => novidadesDaRota(location.pathname), [location.pathname]);

    const [aberta, setAberta] = useState<Novidade | null>(null);
    /* Aberta pela Home, para rever: só o X, e nenhuma marca muda. */
    const [revendo, setRevendo] = useState(false);
    /* Passou em todas as conferências e pode abrir sozinha. */
    const [pronta, setPronta] = useState<Novidade | null>(null);
    const [, setHomeLivre] = useState(homeEstaLivre());
    /* Uma por carregamento da página: vale para todas as novidades, não por chave. */
    const abriuSozinho = useRef(false);
    const refCaixa = useRef<HTMLDivElement>(null);

    /* As conferências: vigente, sem marca no navegador nem no servidor, e a mídia no ar.
       Em modo suporte, nunca — quem está olhando é a Brik, e a marca seria do cliente. */
    useEffect(() => {
        if (!candidatas.length || usuario?.emModoSuporte || usuario?.assinaturaVencida === true) return;
        if (abriuSozinho.current) return;

        /* O navegador responde na hora: tira as já vistas e as adiadas para outro dia. */
        const abertas = candidatas.filter(n => {
            const r = lerRegistro(chaveLocal(usuarioId, n.chave));
            return !r.vista && (r.adiamentos ?? 0) < MAX_ADIAMENTOS && (r.adiadaAte ?? 0) <= Date.now();
        });
        if (!abertas.length) return;

        let vivo = true;
        Promise.all([
            NotificacaoService.novidadesVistas(),
            Promise.all(abertas.map(n => midiaNoAr(n))),
        ]).then(([vistas, noAr]) => {
            if (!vivo) return;
            const escolhida = abertas.find((n, i) => {
                if (vistas?.includes(n.chave)) {
                    /* Visto em outro navegador: anota aqui para não perguntar de novo. */
                    const k = chaveLocal(usuarioId, n.chave);
                    gravarRegistro(k, { ...lerRegistro(k), vista: true });
                    return false;
                }
                return noAr[i] === true;
            });
            if (escolhida) setPronta(escolhida);
        });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidatas, usuarioId]);

    /* A Home avisa quando a apresentação do Brik saiu da frente. */
    useEffect(() => {
        const aoLiberar = () => setHomeLivre(true);
        window.addEventListener(HOME_LIVRE_EVENT, aoLiberar);
        return () => window.removeEventListener(HOME_LIVRE_EVENT, aoLiberar);
    }, []);

    const naHome = location.pathname === '/home';
    const podeAbrir = !!pronta && !abriuSozinho.current && (!naHome || homeEstaLivre());
    useEffect(() => {
        if (!podeAbrir || !pronta) return;
        /* Um respiro depois que a tela chega, para o modal não cair junto com ela. */
        const t = setTimeout(() => {
            abriuSozinho.current = true;
            setRevendo(false);
            setAberta(pronta);
        }, 700);
        return () => clearTimeout(t);
    }, [podeAbrir, pronta]);

    /* O bloco "Novidades" da Home. */
    useEffect(() => {
        const aoPedir = (e: Event) => {
            const chave = (e as CustomEvent<{ chave?: string }>).detail?.chave;
            const alvo = chave
                ? novidadesVigentes().find(n => n.chave === chave)
                : novidadesVigentes().find(n => !n.rotas);
            if (!alvo) return;
            /* Pedido antes de ela abrir sozinha conta como a vez dela: não abre de novo depois. */
            abriuSozinho.current = true;
            setRevendo(true);
            setAberta(alvo);
        };
        window.addEventListener(ABRIR_NOVIDADE_EVENT, aoPedir);
        return () => window.removeEventListener(ABRIR_NOVIDADE_EVENT, aoPedir);
    }, []);

    const marcarVista = useCallback(() => {
        if (!aberta) return;
        const k = chaveLocal(usuarioId, aberta.chave);
        const r = lerRegistro(k);
        if (r.vista) return;
        gravarRegistro(k, { ...r, vista: true });
        /* Se a marca do servidor falhar, a do navegador segura esta aqui. */
        NotificacaoService.marcarNovidadeVista(aberta.chave).catch(() => {});
        /* O vídeo do lançamento faz o papel da apresentação estática do Brik; sem esta marca
           ela apareceria quando a novidade vencesse. A demo de uma tela não apresenta o Brik. */
        if (aberta.video) NotificacaoService.marcarApresentacaoVista().catch(() => {});
    }, [aberta, usuarioId]);

    const entendi = () => { marcarVista(); setAberta(null); };

    const verDepois = useCallback(() => {
        if (!revendo && aberta) {
            const k = chaveLocal(usuarioId, aberta.chave);
            const r = lerRegistro(k);
            if (!r.vista) gravarRegistro(k, { ...r, adiadaAte: amanha(), adiamentos: (r.adiamentos ?? 0) + 1 });
        }
        setAberta(null);
    }, [aberta, revendo, usuarioId]);

    /* Esc fecha só o modal (a mesma captura do Dialog), e conta como "Ver depois". */
    useEffect(() => {
        if (!aberta) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            verDepois();
        };
        document.addEventListener('keydown', aoTeclar, true);
        return () => document.removeEventListener('keydown', aoTeclar, true);
    }, [aberta, verDepois]);

    useEffect(() => { if (aberta) refCaixa.current?.focus(); }, [aberta]);

    if (!aberta) return null;

    const comDemo = !aberta.video && !!aberta.demo;
    const escuro = document.documentElement.getAttribute('data-theme') === 'dark';

    /* Clicar fora NÃO fecha: com vídeo, o clique fora é quase sempre sem querer. */
    return createPortal(
        <div className="nvd-fundo">
            <div
                ref={refCaixa}
                className={`nvd${comDemo ? ' tem-demo' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="nvd-titulo"
                tabIndex={-1}
            >
                <div className="nvd-h">
                    <div className="nvd-h-txt">
                        <span className="nvd-sobre">Novidade no Brik CRM</span>
                        <h2 id="nvd-titulo" className="nvd-t">{aberta.titulo}</h2>
                        <p className="nvd-r">{aberta.resumo}</p>
                    </div>
                    <button type="button" className="nvd-x" onClick={verDepois} aria-label="Fechar">
                        <LuX size={16} />
                    </button>
                </div>

                {comDemo ? (
                    <div className="nvd-corpo">
                        {/* A gravação da própria tela: sem som, em laço, sem controles — ela
                            ilustra os passos abaixo, não é um filme para assistir. Vai no tema
                            de quem está olhando: a mesma tela, gravada duas vezes. */}
                        <video
                            key={`${aberta.chave}-${escuro ? 'escuro' : 'claro'}`}
                            className="nvd-demo"
                            src={(escuro && aberta.demoEscura) || aberta.demo}
                            poster={(escuro && aberta.posterEscuro) || aberta.poster}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            aria-label={`Demonstração: ${aberta.titulo}`}
                        />
                        <ol className="nvd-passos">
                            {aberta.passos?.map((p, i) => (
                                <li key={p.titulo} className="nvd-passo">
                                    <span className="nvd-passo-n" aria-hidden="true">{i + 1}</span>
                                    <div>
                                        <p className="nvd-passo-t">{p.titulo}</p>
                                        <p className="nvd-passo-d">{p.descricao}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                ) : (
                    <video
                        key={aberta.chave}
                        className="nvd-video"
                        src={aberta.video}
                        poster={aberta.poster}
                        controls
                        playsInline
                        preload="metadata"
                        onEnded={marcarVista}
                    >
                        {aberta.legenda && (
                            <track kind="captions" src={aberta.legenda} srcLang="pt-BR" label="Português" default />
                        )}
                    </video>
                )}

                {!revendo ? (
                    <div className="nvd-f">
                        {aberta.rodape && <p className="nvd-nota">{aberta.rodape}</p>}
                        <button type="button" className="nvd-b" onClick={verDepois}>Ver depois</button>
                        <button type="button" className="nvd-b is-primary" onClick={entendi}>Entendi</button>
                    </div>
                ) : aberta.rodape ? (
                    <div className="nvd-f"><p className="nvd-nota">{aberta.rodape}</p></div>
                ) : null}
            </div>
        </div>,
        document.body,
    );
};

export default NovidadeModal;
